import Box from "@/common/components/Box";
import { CopyButton } from "@/common/components/CopyButton";
import RequestNewCardModal from "@/common/components/RequestNewCardModal";
import ResumeItem from "@/common/components/ResumeItem";
import TextField from "@/common/components/TextField";
import { useCardWithSecureInformation } from "@/common/hooks/queries/useCardWithSecureInformation";
import { useCopyButton } from "@/common/hooks/useCopyButton";
import { cardActivationModeType } from "@/common/types/companies/companies.type";
import { formatCardNumber } from "@/common/utils/formatters";
import { cardStatusTranslation } from "@/features/equipments/constants/cardStatusTranslation.constant";
import { useEquipmentRequestNewCard } from "@/features/equipments/hooks/useEquipmentRequestNewCard";
import { EquipmentData } from "@/features/equipments/types/equipments.type";
import { useQueryClient } from "@tanstack/react-query";
import CardManuallyActivateModal from "../../CardManuallyActivateModal";

interface EquipmentCardDetailsProps {
  equipment?: EquipmentData;
  cardManuallyActivateMode?: cardActivationModeType
}

const EquipmentCardDetails = ({ equipment, cardManuallyActivateMode }: EquipmentCardDetailsProps) => {
  const queryClient = useQueryClient();

  const { control, copySuccess, copyFailed } = useCopyButton();

  const {
    mutate: personalCard,
    isPending: personalCardIsPending,
  } = useCardWithSecureInformation({
    onSuccess: (response) => copySuccess(response?.data?.data?.number),
    onError: copyFailed
  });

  const requestNewCardMutation = useEquipmentRequestNewCard({
    equipmentId: equipment?.id,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['equipment', equipment?.id] });
    },
  });

  const showPendingCard = equipment?.card?.status !== 'shipped' && equipment?.card?.status !== 'active'
  const cardToDisplay = showPendingCard ? equipment?.pending_card || equipment?.card : equipment.card;
  const showActivateManuallyCardButton = cardToDisplay?.status === 'shipped' && cardManuallyActivateMode === 'manual'

  return (
    <Box className="flex flex-col gap-6 p-4">
      <div className="flex items-center justify-between">
        <p>Cartão frota</p>

        <div className="flex items-center gap-2">
          <CardManuallyActivateModal
            equipment={equipment}
            card={cardToDisplay}
            showTrigger={showActivateManuallyCardButton}
          />

          <RequestNewCardModal
            mutationHook={requestNewCardMutation}
            operationKlassToCheckPermission='Client::Equipments::RequestNewCard'
          />
        </div>
      </div>

      <div className="flex flex-col gap-3">
        <ResumeItem
          label="Status do cartão"
          value={cardToDisplay?.status && cardStatusTranslation[cardToDisplay?.status as keyof typeof cardStatusTranslation] || '--'}
        />

        <TextField
          disabled
          label="Código"
          className="grow"
          RightIcon={(!personalCardIsPending || !!cardToDisplay?.id) ? <CopyButton control={control} className="text-blue-800" /> : undefined}
          onIconClick={() => personalCard(cardToDisplay?.id!)}
          isLoading={personalCardIsPending}
          value={formatCardNumber(cardToDisplay?.last_four_digits || '')}
        />
      </div>
    </Box>
  );
};

export default EquipmentCardDetails;
